/* eslint-disable */
import React, { useMemo } from 'react'
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material'

const munLabel = (m) => {
  if (!m) return ''
  return m.uf_IBGECod ? `${m.mun_nome} (${m.uf_IBGECod})` : String(m.mun_nome || '')
}

const recLabel = (r) => {
  const ep = String(r?.rec_ep || '').trim() || `Receita ${r?.rec_id}`
  const dt = r?.rec_dtNfPrev ? String(r.rec_dtNfPrev).slice(0, 10) : ''
  return dt ? `${ep} ? ${dt}` : ep
}

export default function SeriesFaturamentoEditor({
  series,
  setSeries,
  muns,
  recs,
  defaultTpl,
}) {
  const munById = useMemo(() => {
    const map = new Map()
    ;(muns || []).forEach((m) => map.set(Number(m.mun_id), m))
    return map
  }, [muns])

  const recById = useMemo(() => {
    const map = new Map()
    ;(recs || []).forEach((r) => map.set(Number(r.rec_id), r))
    return map
  }, [recs])

  const assigned = useMemo(() => {
    const set = new Set()
    ;(series || []).forEach((s) => (s.recIds || []).forEach((id) => set.add(Number(id))))
    return set
  }, [series])

  const unassigned = useMemo(
    () => (recs || []).filter((r) => !assigned.has(Number(r.rec_id))),
    [recs, assigned],
  )

  const patch = (idx, partial) => {
    setSeries((prev) => prev.map((s, i) => (i === idx ? { ...s, ...partial } : s)))
  }

  const setRecIds = (idx, ids) => {
    const want = new Set((ids || []).map(Number).filter((id) => id > 0))
    setSeries((prev) => prev.map((s, i) => {
      if (i === idx) return { ...s, recIds: Array.from(want) }
      return { ...s, recIds: (s.recIds || []).filter((id) => !want.has(Number(id))) }
    }))
  }

  const addSerie = () => {
    setSeries((prev) => [...prev, {
      projFatTpl_id: null,
      projFatTpl_nome: '',
      projFatTpl_nfDescTpl: defaultTpl || '',
      projFatTpl_epPrefix: '',
      projFatTpl_cnpjTom: '',
      mun_id: null,
      projFatTpl_padrao: prev.length === 0 ? 1 : 0,
      recIds: [],
    }])
  }

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>S?ries de NFS-e</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Uma s?rie por munic?pio de presta??o. O mesmo CNPJ em cidades diferentes gera s?ries diferentes.
        Se o distrito n?o coincidir com o munic?pio (ex.: Boa Vista em Catal?o), use o mesmo munic?pio e um nome pr?prio.
      </Typography>

      {(series || []).map((s, idx) => {
        const mun = munById.get(Number(s.mun_id)) || null
        const sameMun = (series || []).filter((x) => Number(x.mun_id) > 0 && Number(x.mun_id) === Number(s.mun_id)).length > 1
        const recValue = (s.recIds || []).map((id) => recById.get(Number(id))).filter(Boolean)
        return (
          <Box key={s.projFatTpl_id || `n-${idx}`} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1.5, mb: 1.5 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1, alignItems: 'flex-start' }}>
              <Autocomplete
                size="small"
                sx={{ minWidth: 280, flex: 1 }}
                options={muns || []}
                value={mun}
                onChange={(e, v) => {
                  const munId = v ? Number(v.mun_id) : null
                  const nomeCidade = v?.mun_nome || ''
                  patch(idx, {
                    mun_id: munId,
                    projFatTpl_nome: String(s.projFatTpl_nome || '').trim() ? s.projFatTpl_nome : nomeCidade,
                    projFatTpl_epPrefix: String(s.projFatTpl_epPrefix || '').trim() ? s.projFatTpl_epPrefix : nomeCidade,
                  })
                }}
                getOptionLabel={munLabel}
                isOptionEqualToValue={(a, b) => Number(a?.mun_id) === Number(b?.mun_id)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label="Munic?pio de presta??o"
                    helperText={sameMun ? 'J? existe outra s?rie neste munic?pio ? conferir se s?o distritos distintos.' : 'Obrigat?rio. Define a s?rie.'}
                  />
                )}
              />
              <TextField
                size="small"
                label="Nome da s?rie"
                value={s.projFatTpl_nome}
                onChange={(e) => patch(idx, { projFatTpl_nome: e.target.value })}
                sx={{ width: 220 }}
              />
              <TextField
                size="small"
                label="CNPJ do tomador"
                value={s.projFatTpl_cnpjTom}
                onChange={(e) => patch(idx, { projFatTpl_cnpjTom: e.target.value })}
                helperText="Pode repetir entre cidades"
                sx={{ width: 200 }}
              />
              <FormControlLabel
                control={(
                  <Checkbox
                    size="small"
                    checked={Number(s.projFatTpl_padrao) === 1}
                    onChange={(e) => setSeries((prev) => prev.map((x, i) => ({
                      ...x,
                      projFatTpl_padrao: i === idx && e.target.checked ? 1 : 0,
                    })))}
                  />
                )}
                label="Padr?o"
              />
              <Button size="small" onClick={() => setSeries((prev) => prev.filter((_, i) => i !== idx))}>
                Remover
              </Button>
            </Box>
            <TextField
              fullWidth
              multiline
              minRows={2}
              size="small"
              label="Template da descri??o desta s?rie"
              value={s.projFatTpl_nfDescTpl}
              onChange={(e) => patch(idx, { projFatTpl_nfDescTpl: e.target.value })}
              helperText="Chaves: {n?m do m?s}, {m?s/ano do BM}, {Valor bruto da medi??o}, {PO}, {FRS}, {ITM}."
              sx={{ mb: 1 }}
            />
            <Autocomplete
              multiple
              size="small"
              options={recs || []}
              value={recValue}
              onChange={(e, v) => setRecIds(idx, (v || []).map((r) => Number(r.rec_id)))}
              getOptionLabel={recLabel}
              isOptionEqualToValue={(a, b) => Number(a?.rec_id) === Number(b?.rec_id)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Receitas desta s?rie"
                  helperText={`${(s.recIds || []).length} vinculada(s). Uma receita s? pode estar em uma s?rie.`}
                />
              )}
            />
          </Box>
        )
      })}

      <Button size="small" variant="outlined" onClick={addSerie} sx={{ mb: 1 }}>
        Adicionar s?rie
      </Button>

      {unassigned.length > 0 && (
        <Box sx={{ mt: 1, mb: 1.5 }}>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Sem s?rie ({unassigned.length})
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Marque a cidade na s?rie e atribua as receitas acima, ou escolha a s?rie aqui.
          </Typography>
          {unassigned.slice(0, 40).map((r) => (
            <Box key={r.rec_id} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
              <Typography variant="caption" sx={{ flex: 1 }}>{recLabel(r)}</Typography>
              <Autocomplete
                size="small"
                sx={{ width: 260 }}
                options={(series || []).map((s, i) => ({ ...s, _i: i }))}
                getOptionLabel={(s) => s.projFatTpl_nome || (munById.get(Number(s.mun_id)) ? munLabel(munById.get(Number(s.mun_id))) : 'S?rie sem nome')}
                isOptionEqualToValue={(a, b) => Number(a?._i) === Number(b?._i)}
                onChange={(e, v) => {
                  if (v == null || v._i == null) return
                  setRecIds(v._i, [...(series[v._i].recIds || []), Number(r.rec_id)])
                }}
                renderInput={(params) => <TextField {...params} label="Vincular a" />}
              />
            </Box>
          ))}
          {unassigned.length > 40 && (
            <Typography variant="caption" color="text.secondary">
              Mostrando 40 de {unassigned.length}. Use o campo de receitas em cada s?rie para o restante.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  )
}

